// File: frontend/src/components/RegionSelectionScreen.js

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import ValuationFrame from "./ValuationFrame";
import { trackFunnelError, trackFunnelStepComplete, trackFunnelStepView } from "../utils/analytics";

const MARKET_POPULATION_BANDS = [
  { value: "250k_plus", label: "Greater than 250k population" },
  { value: "100k_250k", label: "100k to 250k population" },
  { value: "60k_100k", label: "60k to 100k population" },
  { value: "30k_60k", label: "30k to 60k population" },
  { value: "under_30k", label: "Less than 30k population" },
];

const OTHER_MARKET_VALUE = "__other_market__";
const OTHER_MARKET_LABEL = "Other market";

const LEGACY_POPULATION_MARKET_TO_BAND = {
  "<60k >30k population": "30k_60k",
  "<30k population": "under_30k",
};

function RegionSelectionScreen() {
  const navigate = useNavigate();

  useEffect(() => {
    trackFunnelStepView(2, "region_selection");
  }, []);

  const [formData, setFormData] = useState({
    country: localStorage.getItem("country") || "",
    region: localStorage.getItem("region") || "",
    city: localStorage.getItem("city") || "",
    marketPopulationBand: localStorage.getItem("marketPopulationBand") || "",
  });
  const [errors, setErrors] = useState({});
  const [regionOptions, setRegionOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [cityDataStatus, setCityDataStatus] = useState("idle");
  const [usePopulationFallbackMarket, setUsePopulationFallbackMarket] = useState(
    Boolean(localStorage.getItem("marketPopulationBand")) ||
      localStorage.getItem("city") === OTHER_MARKET_LABEL ||
      Boolean(LEGACY_POPULATION_MARKET_TO_BAND[localStorage.getItem("city")])
  );

  const countryRegionMap = {
    USA: [
      "Alabama",
      "Alaska",
      "Arizona",
      "Arkansas",
      "California",
      "Colorado",
      "Connecticut",
      "Delaware",
      "Florida",
      "Georgia",
      "Hawaii",
      "Idaho",
      "Illinois",
      "Indiana",
      "Iowa",
      "Kansas",
      "Kentucky",
      "Louisiana",
      "Maine",
      "Maryland",
      "Massachusetts",
      "Michigan",
      "Minnesota",
      "Mississippi",
      "Missouri",
      "Montana",
      "Nebraska",
      "Nevada",
      "New Hampshire",
      "New Jersey",
      "New Mexico",
      "New York",
      "North Carolina",
      "North Dakota",
      "Ohio",
      "Oklahoma",
      "Oregon",
      "Pennsylvania",
      "Rhode Island",
      "South Carolina",
      "South Dakota",
      "Tennessee",
      "Texas",
      "Utah",
      "Vermont",
      "Virginia",
      "Washington",
      "West Virginia",
      "Wisconsin",
      "Wyoming",
    ],
    Italy: [
      "Lombardy",
      "Lazio",
      "Tuscany",
      "Sicily",
      "Veneto",
      "Emilia-Romagna",
      "Piedmont",
      "Liguria",
      "Marche",
      "Puglia",
      "Calabria",
      "Campania",
      "Basilicata",
      "Abruzzo",
      "Friuli-Venezia Giulia",
      "Trentino-Alto Adige/Sudtirol",
      "Umbria",
      "Molise",
      "Aosta Valley",
    ],
    Australia: [
      "New South Wales",
      "Victoria",
      "Queensland",
      "Western Australia",
      "South Australia",
      "Tasmania",
      "Northern Territory",
      "Australian Capital Territory",
    ],
    Ireland: ["Leinster", "Munster", "Connacht", "Other markets"],
  };

  const regionCityMap = {
    Alabama: ["Birmingham", "Montgomery", "Huntsville"],
    California: ["Los Angeles", "San Francisco", "San Diego", "Sacramento"],
    Florida: ["Miami", "Orlando", "Tampa"],
    "New York": ["New York City", "Buffalo", "Rochester"],
    Texas: ["Dallas", "Houston", "Austin", "San Antonio"],
    Lazio: ["Rome", "Frosinone", "Latina", "Viterbo"],
    "New South Wales": ["Sydney", "Newcastle", "Wollongong"],
    "Australian Capital Territory": ["Canberra"],
    Leinster: ["Dublin", "Dundalk"],
    Connacht: ["Galway"],
    Munster: ["Cork", "Limerick"],
    "Other markets": [],
  };

  useEffect(() => {
    const selectedCountry = formData.country;
    if (selectedCountry && countryRegionMap[selectedCountry]) {
      setRegionOptions(countryRegionMap[selectedCountry]);
    } else {
      setRegionOptions([]);
    }
  }, [formData.country]);

  useEffect(() => {
    const selectedRegion = formData.region;
    if (selectedRegion && regionCityMap[selectedRegion]) {
      setCityOptions(regionCityMap[selectedRegion]);
    } else {
      setCityOptions([]);
    }
  }, [formData.region]);

  useEffect(() => {
    if (
      formData.city &&
      formData.city !== OTHER_MARKET_LABEL &&
      cityOptions.length > 0 &&
      !cityOptions.includes(formData.city) &&
      !LEGACY_POPULATION_MARKET_TO_BAND[formData.city]
    ) {
      setUsePopulationFallbackMarket(true);
      setFormData((prev) => ({
        ...prev,
        city: OTHER_MARKET_LABEL,
      }));
    }
  }, [cityOptions, formData.city]);

  useEffect(() => {
    const { country, region, city } = formData;
    if (!country || !region || !city) {
      setCityDataStatus("idle");
      return;
    }

    if (usePopulationFallbackMarket) {
      setCityDataStatus("limited");
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({ country, region, city });
    setCityDataStatus("checking");

    fetch(`/api/cities/check?${params.toString()}`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("city_check_failed");
        return response.json();
      })
      .then((data) => {
        setCityDataStatus(data.exists ? "available" : "limited");
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setCityDataStatus("limited");
        }
      });

    return () => controller.abort();
  }, [formData.country, formData.region, formData.city, usePopulationFallbackMarket]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "country") {
      setFormData({ country: value, region: "", city: "", marketPopulationBand: "" });
      setUsePopulationFallbackMarket(false);
      setErrors({});
      return;
    }

    if (name === "region") {
      const nextCityOptions = regionCityMap[value] || [];
      const usePopulationFallback = Boolean(value && nextCityOptions.length === 0);
      setFormData((prev) => ({
        ...prev,
        region: value,
        city: usePopulationFallback ? OTHER_MARKET_LABEL : "",
        marketPopulationBand: "",
      }));
      setUsePopulationFallbackMarket(usePopulationFallback);
      setErrors({});
      return;
    }

    if (name === "citySelection") {
      if (value === OTHER_MARKET_VALUE) {
        setUsePopulationFallbackMarket(true);
        setFormData((prev) => ({
          ...prev,
          city: OTHER_MARKET_LABEL,
          marketPopulationBand: "",
        }));
        setErrors({});
        return;
      }

      setUsePopulationFallbackMarket(false);
      setFormData((prev) => ({
        ...prev,
        city: value,
        marketPopulationBand:
          LEGACY_POPULATION_MARKET_TO_BAND[value] || "",
      }));
      setErrors({});
      return;
    }

    if (name === "city") {
      setFormData((prev) => ({
        ...prev,
        city: value || OTHER_MARKET_LABEL,
        marketPopulationBand:
          LEGACY_POPULATION_MARKET_TO_BAND[value] || "",
      }));
      setErrors({});
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors({});
  };

  const handleSubmit = () => {
    const { country, region, city, marketPopulationBand } = formData;
    const newErrors = {};
    const requiresPopulationBand =
      city && (usePopulationFallbackMarket || cityDataStatus === "limited");
    const effectiveMarketPopulationBand = requiresPopulationBand
      ? marketPopulationBand
      : "";

    if (!country) newErrors.country = "Country is required.";
    if (!region) newErrors.region = "Region is required.";
    if (!city) newErrors.city = "Hotel market is required.";
    if (city && !usePopulationFallbackMarket && cityDataStatus === "checking") {
      newErrors.city = "Checking Skyward market data. Please wait a moment.";
    }
    if (requiresPopulationBand && !marketPopulationBand) {
      newErrors.marketPopulationBand = "Population size is required for markets without exact Skyward city data.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      trackFunnelError(2, "region_selection", "validation_failed", {
        error_count: Object.keys(newErrors).length,
      });
      return;
    }

    localStorage.setItem("country", country);
    localStorage.setItem("region", region);
    localStorage.setItem("city", city);
    if (effectiveMarketPopulationBand) {
      localStorage.setItem("marketPopulationBand", effectiveMarketPopulationBand);
    } else {
      localStorage.removeItem("marketPopulationBand");
    }

    trackFunnelStepComplete(2, "region_selection");

    navigate("/demand-drivers");
  };

  const citySelectValue = usePopulationFallbackMarket
    ? OTHER_MARKET_VALUE
    : formData.city;
  const showPopulationBand =
    formData.city &&
    (usePopulationFallbackMarket || cityDataStatus === "limited");

  return (
    <ValuationFrame
      currentStep={1}
      title="Select the Hotel Market"
      subtitle="Choose the country, region, and market first so the valuation can capture the right underwriting context."
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <section className="fieldset">
          <h2 className="fieldset-title">Location</h2>
          <div className="form-stack">
            <label className="form-label field-inline">
              <div className="field-copy">
                <div className="label-text-container">
                  <span className="label-text">Country</span>
                  <span
                    data-tooltip-id="tooltip-country"
                    data-tooltip-content="Select the country where your property is located."
                    aria-label="Tooltip for Country"
                    className="tooltip-icon"
                  >
                    ?
                  </span>
                  <Tooltip id="tooltip-country" place="top" effect="solid" />
                </div>
              </div>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="select-field"
              >
                <option value="">Select Country</option>
                <option value="USA">USA</option>
                <option value="Ireland">Ireland</option>
              </select>
              {errors.country && (
                <p className="error-message">{errors.country}</p>
              )}
            </label>

            <label className="form-label field-inline">
              <div className="field-copy">
                <div className="label-text-container">
                  <span className="label-text">Region / State</span>
                  <span
                    data-tooltip-id="tooltip-region"
                    data-tooltip-content="Select the region or state of your property."
                    aria-label="Tooltip for Region"
                    className="tooltip-icon"
                  >
                    ?
                  </span>
                  <Tooltip id="tooltip-region" place="top" effect="solid" />
                </div>
              </div>
              <select
                name="region"
                value={formData.region}
                onChange={handleChange}
                className="select-field"
                disabled={!formData.country}
              >
                <option value="">Select Region / State</option>
                {regionOptions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
              {errors.region && (
                <p className="error-message">{errors.region}</p>
              )}
            </label>

            <label className="form-label field-inline">
              <div className="field-copy">
                <div className="label-text-container">
                  <span className="label-text">Hotel Market</span>
                  <span
                    data-tooltip-id="tooltip-city"
                    data-tooltip-content="Select a listed market, or choose the population fallback if the market is not listed."
                    aria-label="Tooltip for Hotel Market"
                    className="tooltip-icon"
                  >
                    ?
                  </span>
                  <Tooltip id="tooltip-city" place="top" effect="solid" />
                </div>
              </div>

              {cityOptions.length > 0 ? (
                <select
                  name="citySelection"
                  value={citySelectValue}
                  onChange={handleChange}
                  className="select-field"
                  disabled={!formData.region}
                >
                  <option value="">Select Hotel Market</option>
                  {cityOptions.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                  <option value={OTHER_MARKET_VALUE}>
                    Market not listed - choose population size
                  </option>
                </select>
              ) : null}

              {!cityOptions.length && formData.region ? (
                <p className="helper">
                  This market is not in the exact Skyward city list yet. Choose the population size below.
                </p>
              ) : null}
              {errors.city && <p className="error-message">{errors.city}</p>}
            </label>

            {showPopulationBand && (
              <label className="form-label field-inline">
                <div className="field-copy">
                  <div className="label-text-container">
                    <span className="label-text">Population Size</span>
                    <span
                      data-tooltip-id="tooltip-market-population-band"
                      data-tooltip-content="Used only when Skyward does not have an exact cap-rate record for the selected market."
                      aria-label="Tooltip for Population Size"
                      className="tooltip-icon"
                    >
                      ?
                    </span>
                    <Tooltip
                      id="tooltip-market-population-band"
                      place="top"
                      effect="solid"
                    />
                  </div>
                  <p className="helper">
                    Select the closest market population band. This drives the cap-rate fallback when there is no exact city record.
                  </p>
                </div>
                <select
                  name="marketPopulationBand"
                  value={formData.marketPopulationBand}
                  onChange={handleChange}
                  className="select-field"
                >
                  <option value="">Select population band</option>
                  {MARKET_POPULATION_BANDS.map((band) => (
                    <option key={band.value} value={band.value}>
                      {band.label}
                    </option>
                  ))}
                </select>
                {errors.marketPopulationBand && (
                  <p className="error-message">{errors.marketPopulationBand}</p>
                )}
              </label>
            )}
          </div>
        </section>

        <div className="button-row">
          <button
            type="button"
            className="button secondary-button"
            onClick={() => navigate("/")}
          >
            Back
          </button>
          <button type="button" className="button" onClick={handleSubmit}>
            Continue
          </button>
        </div>
      </form>
    </ValuationFrame>
  );
}

export default RegionSelectionScreen;
